/**
 * Semantic color tokens — the ONE color source shared by web and mobile.
 *
 * Name a color by its JOB (page ground, card, muted text, danger), never by its
 * hue or depth, so a theme can change what a job looks like without renaming
 * anything. `colorTokens.light` and `colorTokens.dark` have identical keys; the
 * compile-time guards at the bottom fail the build if they ever drift.
 *
 * The standard (Alex, 2026-10-24, "option B"): the mobile app's look.
 *   - Dark  = the mobile app's slate darkColors (blue-tinted grays on black).
 *   - Light = the mobile app's lightColors.
 *   - Brand = scarlet #E3522E; solid fills under white text use #C84826
 *     (pressed #B23E1F) so white clears WCAG AA (4.8:1).
 *
 * How each platform reads them:
 *   - Web: `tokenVars.*` (e.g. `tokenVars.surface`) is `var(--tc-color-surface,
 *     <dark value>)`. The variables are written by generateThemeCSS() in
 *     ./cssVars from this file, so `<html data-theme>` switches them.
 *   - Mobile: apps/mobile/src/theme/colors.ts derives its overlapping palette
 *     keys from these values (see MOBILE_TOKEN_ALIASES there), and
 *     `useAppTokens()` returns the active set.
 *
 * This file has NO imports so React Native can deep-import it
 * (`@turn-connect/ui/src/theme/tokens`) without pulling in web-only code.
 * Reference with values: ./README.md.
 */

export interface ColorTokens {
  /** Page ground: the screen behind everything. */
  bg: string;
  /** Secondary ground: inputs, sheets/dialog bodies, grouped sections on bg. */
  bgSubtle: string;
  /** Card / panel ground. */
  surface: string;
  /** Quiet neutral fill: tags, inset wells, inactive chips. */
  surfaceMuted: string;
  /** Above a card: menus, popovers, secondary buttons, hovered rows. */
  surfaceRaised: string;
  /** Default border: cards, inputs, buttons. */
  border: string;
  /** Hairline divider: list separators, header rules. */
  borderSubtle: string;
  /** Emphasised border: hovered input, a divider that must read. */
  borderStrong: string;
  /** Focus ring and the selected item's border. */
  focus: string;
  text: {
    /** Body copy and headings. */
    primary: string;
    /** Supporting copy: descriptions, secondary values. */
    secondary: string;
    /** Labels, captions, metadata. AA on surface, bgSubtle, bg and the header `band` in both themes. */
    muted: string;
    /** Disabled controls only (below AA by design). */
    disabled: string;
  };
  /** Content ON a solid accent fill (brandFill, danger, success). White in both themes. */
  onAccent: string;
  /** A light tint ON a solid accent fill: the count badge inside the chosen tab. */
  onAccentSubtle: string;
  /** Brand scarlet: logo, active indicator, accent icons. Not for small text or fills under text. */
  brand: string;
  /** Solid scarlet under onAccent text: primary buttons, filled badges. */
  brandFill: string;
  /** brandFill while pressed / hovered. */
  brandFillPressed: string;
  /** Scarlet-tinted ground: a selected brand row, the viewer's own message. */
  brandSubtle: string;
  /** Small attention text and text links (past due, "Change"). AA in both themes. */
  textAccent: string;
  /** Done / paid / healthy. */
  success: string;
  successSubtle: string;
  /** Needs attention soon / pending: icons, borders, large figures. */
  warning: string;
  warningSubtle: string;
  /** Small warning TEXT (a pill, a note). AA on bg, surface and warningSubtle. */
  warningText: string;
  /** Errors, destructive actions, overdue: icons, borders, fills. */
  danger: string;
  dangerSubtle: string;
  /** Small error TEXT (a form error, a failed load). AA on bg, surface and dangerSubtle. */
  dangerText: string;
  /** Neutral information, in-progress. */
  info: string;
  infoSubtle: string;
  /** Modal / sheet backdrop. Darkens in both themes. */
  overlay: string;
  /** Barely-there fill over the current ground (hover). Inverts with the theme. */
  wash: string;
  /** Pressed / selected fill over the current ground. Inverts with the theme. */
  washStrong: string;
  /**
   * The light plate a shop's logo IMAGE sits on (LogoMark), in BOTH themes:
   * logos are drawn for white paper, and a transparent dark logo vanishes on
   * a dark card. The logo itself is never recoloured.
   */
  logoPlate: string;
  /**
   * The plate behind a shop's initials when it has no logo (LogoMark, and a
   * location group's icon). Follows the theme: a dark plate in dark mode, so
   * a column of initials does not glare white.
   */
  initialsPlate: string;
  /** The frame around the initials plate: visible on `bg` and `surface`. */
  initialsPlateBorder: string;
  /** The initials (and group icon) on `initialsPlate` (AA on it). */
  onInitialsPlate: string;
  /**
   * A header band: the shaded strip across the top of a section box, a table's
   * header row, a totals row. Clearly apart from the `surface` body in both
   * themes; `text.*` stays AA on it.
   */
  band: string;
  /** A header band that is a button, while hovered or pressed. */
  bandHover: string;
  /** Categorical accents for telling cards apart (not status). */
  category: {
    teal: string;
    purple: string;
    /** Tint behind a teal accent icon (Vehicles). */
    tealSubtle: string;
    /** Tint behind a purple accent icon (Invoices). */
    purpleSubtle: string;
    indigo: string;
    /** Tint behind an indigo accent icon (My time). */
    indigoSubtle: string;
    pink: string;
    /** Tint behind a pink accent icon (Mentions). */
    pinkSubtle: string;
  };
}

/** Every dotted token path: 'bg' | 'text.muted' | 'category.teal' | … */
type PathsOf<T> = {
  [K in keyof T & string]: T[K] extends string ? K : `${K}.${PathsOf<T[K]>}`;
}[keyof T & string];
export type TokenPath = PathsOf<ColorTokens>;

/** Dark — the mobile app's slate darkColors. */
const dark: ColorTokens = {
  bg: '#000000',
  bgSubtle: '#111827',
  surface: '#1F2937',
  surfaceMuted: '#1F2937',
  surfaceRaised: '#2A2A2A',
  border: '#374151',
  borderSubtle: '#333333',
  borderStrong: '#4B5563',
  focus: '#60A5FA',
  text: {
    primary: '#FFFFFF',
    secondary: '#E5E7EB',
    muted: '#9CA3AF',
    disabled: '#6B7280',
  },
  onAccent: '#FFFFFF',
  onAccentSubtle: 'rgba(255, 255, 255, 0.22)',
  brand: '#E3522E',
  brandFill: '#C84826',
  brandFillPressed: '#B23E1F',
  brandSubtle: 'rgba(227, 82, 46, 0.18)',
  textAccent: '#F0714F',
  success: '#10B981',
  successSubtle: 'rgba(16, 185, 129, 0.15)',
  warning: '#F59E0B',
  warningSubtle: 'rgba(245, 158, 11, 0.15)',
  warningText: '#F59E0B',
  danger: '#EF4444',
  dangerSubtle: 'rgba(239, 68, 68, 0.15)',
  dangerText: '#F87171',
  info: '#3B82F6',
  infoSubtle: 'rgba(59, 130, 246, 0.20)',
  overlay: 'rgba(0, 0, 0, 0.70)',
  wash: 'rgba(255, 255, 255, 0.04)',
  washStrong: 'rgba(255, 255, 255, 0.10)',
  logoPlate: '#F4F4F5',
  initialsPlate: '#111827',
  initialsPlateBorder: '#4B5563',
  onInitialsPlate: '#E5E7EB',
  band: '#111827',
  bandHover: '#2A2A2A',
  category: {
    teal: '#14B8A6',
    purple: '#8B5CF6',
    tealSubtle: 'rgba(20, 184, 166, 0.15)',
    purpleSubtle: 'rgba(139, 92, 246, 0.15)',
    indigo: '#818CF8',
    indigoSubtle: 'rgba(129, 140, 248, 0.15)',
    pink: '#F472B6',
    pinkSubtle: 'rgba(244, 114, 182, 0.15)',
  },
};

/** Light — the mobile app's lightColors. */
const light: ColorTokens = {
  bg: '#F4F4F5',
  bgSubtle: '#FFFFFF',
  surface: '#FFFFFF',
  surfaceMuted: '#ECECED',
  surfaceRaised: '#F4F4F5',
  border: '#D6D6DA',
  borderSubtle: '#E4E4E7',
  borderStrong: '#C4C4CC',
  focus: '#2563EB',
  text: {
    primary: '#18181B',
    secondary: '#3F3F46',
    muted: '#63636B',
    disabled: '#A1A1AA',
  },
  onAccent: '#FFFFFF',
  onAccentSubtle: 'rgba(255, 255, 255, 0.22)',
  brand: '#E3522E',
  brandFill: '#C84826',
  brandFillPressed: '#B23E1F',
  brandSubtle: 'rgba(227, 82, 46, 0.10)',
  textAccent: '#B23E1F',
  success: '#059669',
  successSubtle: 'rgba(5, 150, 105, 0.14)',
  warning: '#D97706',
  warningSubtle: 'rgba(217, 119, 6, 0.14)',
  warningText: '#92400E',
  danger: '#DC2626',
  dangerSubtle: 'rgba(220, 38, 38, 0.12)',
  dangerText: '#B91C1C',
  info: '#2563EB',
  infoSubtle: 'rgba(37, 99, 235, 0.10)',
  overlay: 'rgba(0, 0, 0, 0.45)',
  wash: 'rgba(0, 0, 0, 0.04)',
  washStrong: 'rgba(0, 0, 0, 0.07)',
  logoPlate: '#FFFFFF',
  initialsPlate: '#FFFFFF',
  initialsPlateBorder: '#D6D6DA',
  onInitialsPlate: '#3F3F46',
  band: '#ECECEF',
  bandHover: '#E4E4E8',
  category: {
    teal: '#0D9488',
    purple: '#7C3AED',
    tealSubtle: 'rgba(13, 148, 136, 0.12)',
    purpleSubtle: 'rgba(124, 58, 237, 0.10)',
    indigo: '#4F46E5',
    indigoSubtle: 'rgba(79, 70, 229, 0.10)',
    pink: '#DB2777',
    pinkSubtle: 'rgba(219, 39, 119, 0.10)',
  },
};

export type TokenScheme = 'light' | 'dark';

export const colorTokens: Readonly<Record<TokenScheme, ColorTokens>> = Object.freeze({ light, dark });

/** Dotted token path → value, e.g. { 'text.muted': '#9CA3AF', surface: '#1F2937' }. */
export function flattenTokens(set: ColorTokens): Record<string, string> {
  const out: Record<string, string> = {};
  const walk = (obj: Record<string, unknown>, prefix: string) => {
    for (const [key, value] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (value && typeof value === 'object') walk(value as Record<string, unknown>, path);
      else out[path] = String(value);
    }
  };
  walk(set as unknown as Record<string, unknown>, '');
  return out;
}

/** The CSS custom property for a dotted token path: 'text.muted' → '--tc-color-text-muted'. */
export const tokenCssVar = (path: TokenPath | string): string => `--tc-color-${path.replace(/\./g, '-')}`;

/**
 * The web-facing token set: same shape as ColorTokens, every leaf is
 * `var(--tc-color-…, <dark value>)`. The dark fallback means a paint before the
 * variable stylesheet loads still renders the standard dark look.
 */
function toVars<T>(obj: T, prefix = ''): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${key}` : key;
    out[key] = value && typeof value === 'object' ? toVars(value, path) : `var(${tokenCssVar(path)}, ${value})`;
  }
  return out as T;
}

export const tokenVars: ColorTokens = toVars(dark);
